<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="7">
            <h1 class="h4 mb-1 font-weight-bold">Staff</h1>
            <p class="text-muted mb-0">Your team, payroll and claims at a glance</p>
          </CCol>
          <CCol md="5" class="d-flex flex-wrap justify-content-md-end mt-3 mt-md-0">
            <CButton color="light" class="mr-2 mb-2 mb-md-0" to="/tenants/claimList">
              <CIcon name="cil-task" class="mr-1" /> Review claims
            </CButton>
            <CButton color="light" class="mr-2 mb-2 mb-md-0" :to="{ name: 'Employee' }">
              <CIcon name="cil-user-plus" class="mr-1" /> Add employee
            </CButton>
            <CButton color="primary" class="mb-2 mb-md-0" to="/tenants/payroll">
              <CIcon name="cil-plus" class="mr-1" /> Run payroll
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Error -->
    <CAlert v-if="loadError" color="danger" class="d-flex justify-content-between align-items-center">
      <span>Couldn't load your staff figures. Check your connection and try again.</span>
      <CButton color="light" size="sm" @click="loadDashboardData">
        <CIcon name="cil-reload" /> Try again
      </CButton>
    </CAlert>

    <!-- Key figures -->
    <CRow>
      <CCol sm="6" lg="4">
        <CCard class="mb-4">
          <CCardBody>
            <div class="text-muted small">Active employees</div>
            <div class="h4 font-weight-bold mb-0">
              <CSpinner v-if="loading" size="sm" color="primary" />
              <template v-else>{{ activeEmployees }}</template>
            </div>
            <small class="text-muted">{{ totalEmployees }} on record</small>
          </CCardBody>
          <CCardFooter class="bg-transparent pt-0 border-0">
            <router-link to="/tenants/employeeList" class="small">View employees</router-link>
          </CCardFooter>
        </CCard>
      </CCol>

      <CCol sm="6" lg="4">
        <CCard class="mb-4">
          <CCardBody>
            <div class="text-muted small">Net pay</div>
            <div class="h4 font-weight-bold mb-0">
              <CSpinner v-if="loading" size="sm" color="primary" />
              <template v-else>{{ money(monthlyPayroll) }}</template>
            </div>
            <small class="text-muted">This month · {{ monthlyPayrollCount }} payroll{{ monthlyPayrollCount === 1 ? "" : "s" }}</small>
          </CCardBody>
          <CCardFooter class="bg-transparent pt-0 border-0">
            <router-link to="/tenants/payrollList" class="small">View payroll</router-link>
          </CCardFooter>
        </CCard>
      </CCol>

      <CCol sm="6" lg="4">
        <CCard class="mb-4">
          <CCardBody>
            <div class="text-muted small">Payslips</div>
            <div class="h4 font-weight-bold mb-0">
              <CSpinner v-if="loading" size="sm" color="primary" />
              <template v-else>{{ payslipsGenerated }}</template>
            </div>
            <small class="text-muted">This month</small>
          </CCardBody>
          <CCardFooter class="bg-transparent pt-0 border-0">
            <router-link to="/tenants/paySlipList" class="small">View payslips</router-link>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>

    <!-- Recent activity -->
    <CRow>
      <CCol lg="6">
        <CCard class="mb-4">
          <CCardHeader><strong>Newest employees</strong></CCardHeader>
          <CCardBody>
            <div v-if="loading" class="text-center py-3"><CSpinner color="primary" /></div>
            <div v-else-if="!recentEmployees.length" class="text-center py-3">
              <p class="text-muted mb-2">No employees yet.</p>
              <CButton color="primary" :to="{ name: 'Employee' }">
                <CIcon name="cil-user-plus" /> Add employee
              </CButton>
            </div>
            <CListGroup v-else flush>
              <CListGroupItem
                v-for="employee in recentEmployees"
                :key="employee.id"
                :to="{ name: 'EmployeeById', params: { id: employee.id } }"
                class="d-flex justify-content-between align-items-center"
              >
                <div class="text-truncate mr-2">
                  <div class="font-weight-bold text-truncate">{{ employee.name }}</div>
                  <small class="text-muted">{{ employee.detail }}</small>
                </div>
                <small class="text-muted text-nowrap">{{ employee.joined }}</small>
              </CListGroupItem>
            </CListGroup>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="6">
        <CCard class="mb-4">
          <CCardHeader><strong>Latest payroll</strong></CCardHeader>
          <CCardBody>
            <div v-if="loading" class="text-center py-3"><CSpinner color="primary" /></div>
            <div v-else-if="!recentPayrolls.length" class="text-center py-3">
              <p class="text-muted mb-2">No payroll yet.</p>
              <CButton color="primary" to="/tenants/payroll">
                <CIcon name="cil-plus" /> Run payroll
              </CButton>
            </div>
            <CListGroup v-else flush>
              <CListGroupItem
                v-for="payroll in recentPayrolls"
                :key="payroll.id"
                :to="`/tenants/Payroll/${payroll.id}`"
                class="d-flex justify-content-between align-items-center"
              >
                <div class="text-truncate mr-2">
                  <div class="font-weight-bold text-truncate">{{ payroll.employee }}</div>
                  <small class="text-muted">{{ payroll.date }} · {{ money(payroll.amount) }}</small>
                </div>
                <CBadge :color="hrStatusColor('payroll', payroll.status)">
                  {{ hrStatusLabel('payroll', payroll.status) }}
                </CBadge>
              </CListGroupItem>
            </CListGroup>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import ProfileApi from "@/lib/profileApi";
import PayrollApi from "@/lib/payrollApi";
import PaySlipApi from "@/lib/paySlipApi";
import { formatMyr } from "@/utils/money";
import { formatDate, asDateString, mytToday } from "@/utils/datetime";
import { hrStatusMixin } from "@/utils/hrStatusBadges";

export default {
  name: "StaffDashboard",
  mixins: [hrStatusMixin],
  data() {
    return {
      loading: true,
      loadError: false,
      totalEmployees: 0,
      activeEmployees: 0,
      monthlyPayroll: 0,
      monthlyPayrollCount: 0,
      payslipsGenerated: 0,
      recentEmployees: [],
      recentPayrolls: [],
      profileApi: new ProfileApi(),
      payrollApi: new PayrollApi(),
      paySlipApi: new PaySlipApi(),
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Staff", "Dashboard"]);
    this.loadDashboardData();
  },
  methods: {
    money(amount) {
      return `RM ${formatMyr(amount)}`;
    },
    async loadDashboardData() {
      this.loading = true;
      this.loadError = false;
      try {
        // Current month in Malaysia time, as "yyyy-MM"
        const thisMonth = mytToday().substring(0, 7);
        const inThisMonth = (value) => asDateString(value).substring(0, 7) === thisMonth;
        const byDateDesc = (field) => (a, b) => asDateString(b[field]).localeCompare(asDateString(a[field]));

        const [employeeResponse, payrollResponse, payslipResponse] = await Promise.all([
          this.profileApi.getListByCurrentBusiness(false),
          this.payrollApi.getListByCurrentBusiness(),
          this.paySlipApi.getListByCurrentBusiness(),
        ]);

        const employees = employeeResponse.result || [];
        this.totalEmployees = employees.length;
        this.activeEmployees = employees.filter((e) => (e.employmentStatus || "Active") === "Active").length;
        this.recentEmployees = [...employees]
          .sort(byDateDesc("joinDate"))
          .slice(0, 5)
          .map((emp) => ({
            id: emp.id,
            name: emp.fullName || emp.email,
            detail: emp.position || emp.email || "",
            joined: emp.joinDate ? `Joined ${formatDate(emp.joinDate)}` : "",
          }));

        const allPayrolls = payrollResponse.result || [];
        const monthPayrolls = allPayrolls.filter(
          (p) => inThisMonth(p.date) && p.statusDescription !== "Cancelled"
        );
        this.monthlyPayroll = monthPayrolls.reduce((sum, p) => sum + (parseFloat(p.netAmount) || 0), 0);
        this.monthlyPayrollCount = monthPayrolls.length;
        this.recentPayrolls = [...allPayrolls]
          .sort(byDateDesc("date"))
          .slice(0, 5)
          .map((p) => ({
            id: p.id,
            employee: (p.profile && p.profile.fullName) || p.profileName || p.payrollNo,
            date: formatDate(p.date),
            amount: p.netAmount || 0,
            status: p.statusDescription,
          }));

        const allPayslips = payslipResponse.result || [];
        this.payslipsGenerated = allPayslips.filter((ps) => inThisMonth(ps.date)).length;
      } catch (error) {
        console.error("Dashboard load error:", error);
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
